import React, { useEffect, useMemo, useRef } from "react";
import { Accordion, Button, Row, Col } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import { RecekOnixItemPicker } from "../inputs";
import { useOnixOptions } from "../../hooks/title/useOnixOptions";
import { filterByPrimaryContentType, filterFormDetailsByForm, isAllowedByPrimaryContentType, isEbookForm, isFormDetailAllowedByForm, } from "../../utils/onixDependencies";
import { withPreferredFirst } from "../../utils/selectMappers";
import { buildPrimaryContentTypeItems, MUSIC_NOTATION_CONTENT_TYPE, } from "../../utils/productContentType";
var MUSIC_NOTATION = MUSIC_NOTATION_CONTENT_TYPE;
/**
 * ONIX Product form (codetable 150) codes surfaced at the top of the Form
 * picker as the most frequently used forms: "BB" (vázaná / hardback), "BC"
 * (brožovaná / paperback) and "EA" (e-kniha / e-book), in that order.
 */
var PREFERRED_FORM_CODES = ["BB", "BC", "EA"];
/**
 * Explicit empty value for clearing an ONIX picker field. `setValue(name,
 * undefined)` is not enough: react-hook-form then falls back to the value the
 * field was mounted with (e.g. the stored product seeded via reset()), so the
 * picker would keep showing, and submit, the old code. `null` is treated as
 * missing by the product schema (`requiredOnixItem`) and by the picker.
 */
var CLEARED_ONIX_ITEM = null;
export var ProductGeneralSection = function (_a) {
    var _b, _c;
    var open = _a.open, onToggle = _a.onToggle;
    var formatMessage = useIntl().formatMessage;
    var _d = useFormContext(), control = _d.control, watch = _d.watch, setValue = _d.setValue, getValues = _d.getValues;
    var _e = useOnixOptions(), primaryContentTypeTable = _e.primaryContentTypeTable, formTable = _e.formTable, formDetailTable = _e.formDetailTable, primaryContentTypeStatus = _e.primaryContentTypeStatus, formStatus = _e.formStatus, formDetailStatus = _e.formDetailStatus;
    var productId = watch("id");
    var primaryContentTypeValue = (_b = watch("primaryContentType")) === null || _b === void 0 ? void 0 : _b.value;
    var formValue = (_c = watch("form")) === null || _c === void 0 ? void 0 : _c.value;
    var filteredPrimaryContentTypeItems = useMemo(function () {
        var _a;
        return buildPrimaryContentTypeItems((_a = primaryContentTypeTable === null || primaryContentTypeTable === void 0 ? void 0 : primaryContentTypeTable.onixCodetableItems) !== null && _a !== void 0 ? _a : [], { isNewProduct: !productId, currentValue: primaryContentTypeValue });
    }, [
        primaryContentTypeTable === null || primaryContentTypeTable === void 0 ? void 0 : primaryContentTypeTable.onixCodetableItems,
        primaryContentTypeValue,
        productId,
    ]);
    var filteredFormItems = useMemo(function () {
        var _a;
        return withPreferredFirst(filterByPrimaryContentType((_a = formTable === null || formTable === void 0 ? void 0 : formTable.onixCodetableItems) !== null && _a !== void 0 ? _a : [], primaryContentTypeValue, "form"), PREFERRED_FORM_CODES);
    }, [formTable === null || formTable === void 0 ? void 0 : formTable.onixCodetableItems, primaryContentTypeValue]);
    var filteredFormDetailItems = useMemo(function () {
        var _a;
        return filterFormDetailsByForm(filterByPrimaryContentType((_a = formDetailTable === null || formDetailTable === void 0 ? void 0 : formDetailTable.onixCodetableItems) !== null && _a !== void 0 ? _a : [], primaryContentTypeValue, "formDetail"), formValue);
    }, [formDetailTable === null || formDetailTable === void 0 ? void 0 : formDetailTable.onixCodetableItems, primaryContentTypeValue, formValue]);
    var _f = useFieldArray({
        control: control,
        name: "formDetails",
    }), formDetailFields = _f.fields, appendFormDetail = _f.append, removeFormDetail = _f.remove;
    var prevPrimaryContentTypeRef = useRef(primaryContentTypeValue);
    useEffect(function () {
        var _a, _b;
        if (prevPrimaryContentTypeRef.current === primaryContentTypeValue)
            return;
        prevPrimaryContentTypeRef.current = primaryContentTypeValue;
        var currentFormCode = (_a = getValues("form")) === null || _a === void 0 ? void 0 : _a.value;
        if (currentFormCode &&
            !isAllowedByPrimaryContentType(currentFormCode, primaryContentTypeValue, "form")) {
            setValue("form", CLEARED_ONIX_ITEM, { shouldDirty: true });
        }
        ((_b = getValues("formDetails")) !== null && _b !== void 0 ? _b : []).forEach(function (detail, index) {
            var _a;
            var detailCode = (_a = detail === null || detail === void 0 ? void 0 : detail.value) === null || _a === void 0 ? void 0 : _a.value;
            if (detailCode &&
                !isAllowedByPrimaryContentType(detailCode, primaryContentTypeValue, "formDetail")) {
                setValue("formDetails.".concat(index, ".value"), CLEARED_ONIX_ITEM, {
                    shouldDirty: true,
                });
            }
        });
    }, [primaryContentTypeValue, getValues, setValue]);
    // Clears only the form details the newly picked form no longer allows. Runs
    // from the picker's onChange (a user action), so seeding the form with an
    // existing product via reset() never wipes its stored details.
    var clearFormDetailsIncompatibleWithForm = function (formCode) {
        var _a;
        ((_a = getValues("formDetails")) !== null && _a !== void 0 ? _a : []).forEach(function (detail, index) {
            var _a;
            var detailCode = (_a = detail === null || detail === void 0 ? void 0 : detail.value) === null || _a === void 0 ? void 0 : _a.value;
            if (detailCode && !isFormDetailAllowedByForm(detailCode, formCode)) {
                setValue("formDetails.".concat(index, ".value"), CLEARED_ONIX_ITEM, {
                    shouldDirty: true,
                });
            }
        });
    };
    var handleAddFormDetail = function () {
        appendFormDetail({
            value: undefined,
        });
    };
    // Picking an e-book form offers its file format right away: one optional,
    // empty form detail row is added when there is none. Runs only from the
    // picker's onChange, so loading an existing product via reset() adds nothing.
    var appendFormDetailForEbookForm = function (formCode) {
        var _a;
        if (isEbookForm(formCode) &&
            ((_a = getValues("formDetails")) !== null && _a !== void 0 ? _a : []).length === 0) {
            handleAddFormDetail();
        }
    };
    return (React.createElement(Accordion, { label: formatMessage({
            id: "ui-br-agency.product.accordion.general",
        }), open: open, onToggle: onToggle },
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement(Controller, { control: control, name: "primaryContentType", render: function (_a) {
                        var _b;
                        var field = _a.field, fieldState = _a.fieldState;
                        return (React.createElement(RecekOnixItemPicker, { labelId: "ui-br-agency.product.primaryContentType.field.label", dataOptions: filteredPrimaryContentTypeItems, codetableStatus: primaryContentTypeStatus, value: field.value, onChange: field.onChange, error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message, required: true, disabled: !!productId && primaryContentTypeValue === MUSIC_NOTATION }));
                    } }))),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement(Controller, { control: control, name: "form", render: function (_a) {
                        var _b;
                        var field = _a.field, fieldState = _a.fieldState;
                        return (React.createElement(RecekOnixItemPicker, { labelId: "ui-br-agency.product.form.field.label", dataOptions: filteredFormItems, codetableStatus: formStatus, value: field.value, onChange: function (selected) {
                                field.onChange(selected);
                                clearFormDetailsIncompatibleWithForm(selected === null || selected === void 0 ? void 0 : selected.value);
                                appendFormDetailForEbookForm(selected === null || selected === void 0 ? void 0 : selected.value);
                            }, error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message, required: true }));
                    } }))),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement("h5", null,
                    React.createElement(FormattedMessage, { id: "ui-br-agency.product.formDetails.label" })),
                formDetailFields.map(function (field, index) { return (React.createElement(Row, { key: field.id, style: { marginBottom: "10px" } },
                    React.createElement(Col, { xs: 10 },
                        React.createElement(Controller, { control: control, name: "formDetails.".concat(index, ".value"), render: function (_a) {
                                var _b;
                                var controllerField = _a.field, fieldState = _a.fieldState;
                                return (React.createElement(RecekOnixItemPicker, { labelId: "ui-br-agency.product.formDetail.value.label", dataOptions: filteredFormDetailItems, codetableStatus: formDetailStatus, value: controllerField.value, onChange: controllerField.onChange, error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message }));
                            } })),
                    React.createElement(Col, { xs: 2 },
                        React.createElement(Button, { buttonStyle: "danger", marginBottom0: true, onClick: function () { return removeFormDetail(index); } },
                            React.createElement(FormattedMessage, { id: "ui-br-agency.button.remove" }))))); }),
                React.createElement(Button, { buttonStyle: "default", onClick: handleAddFormDetail },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.product.formDetail.add" }))))));
};
